import React, { useState, useRef, useEffect } from 'react';
import { useWallet } from './WalletContext';
import { web } from '@klever/sdk-web';
import {
  FaTimes,
  FaCog,
  FaImage,
  FaLink,
  FaPercent,
  FaPause,
  FaPlay,
  FaUserPlus,
  FaUserMinus,
  FaExchangeAlt,
  FaLock,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaInfoCircle,
  FaLayerGroup,
  FaExternalLinkAlt,
  FaPlus,
  FaBan
} from 'react-icons/fa';
import './ManageCollectionModal.css';

// AssetTrigger types
const TRIGGER_TYPES = {
  MINT: 0,
  BURN: 1,
  WIPE: 2,
  PAUSE: 3,
  RESUME: 4,
  CHANGE_OWNER: 5,
  ADD_ROLE: 6,
  REMOVE_ROLE: 7,
  UPDATE_METADATA: 8,
  STOP_NFT_MINT: 9,
  UPDATE_LOGO: 10,
  UPDATE_URIS: 11,
  CHANGE_ROYALTIES_RECEIVER: 12,
  UPDATE_STAKING: 13,
  UPDATE_ROYALTIES: 14,
  UPDATE_KDA_FEE_POOL: 15,
  STOP_ROYALTIES_CHANGE: 16,
  STOP_NFT_METADATA_CHANGE: 17
};

const ManageCollectionModal = ({ collection, onClose, onSuccess }) => {
  const { walletAddress } = useWallet();
  const modalRef = useRef(null);

  // Active action tab
  const [activeAction, setActiveAction] = useState('overview');

  // Form states for different actions
  const [logoUrl, setLogoUrl] = useState(collection?.logo || '');
  const [metadataUri, setMetadataUri] = useState('');
  const [royaltyPercentage, setRoyaltyPercentage] = useState(
    collection?.royalties?.marketPercentage ? collection.royalties.marketPercentage / 100 : 5
  );
  const [royaltyReceiver, setRoyaltyReceiver] = useState(
    collection?.royalties?.address || walletAddress || ''
  );
  const [newOwner, setNewOwner] = useState('');
  const [roleAddress, setRoleAddress] = useState('');
  const [roleType, setRoleType] = useState('mint');
  const [mintAmount, setMintAmount] = useState(1);
  const [mintReceiver, setMintReceiver] = useState(walletAddress || '');

  // Transaction state
  const [transactionState, setTransactionState] = useState('');
  const [transactionMessage, setTransactionMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (transactionState !== 'processing') {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('modal-open');
    };
  }, [onClose, transactionState]);

  // Initialize metadata URI from collection
  useEffect(() => {
    if (collection?.uris) {
      const metadataObj = collection.uris.find(u => u.key === 'metadata');
      if (metadataObj) {
        setMetadataUri(metadataObj.value);
      }
    }
  }, [collection]);

  // Execute AssetTrigger transaction
  const executeAssetTrigger = async (triggerType, additionalPayload = {}) => {
    setTransactionState('processing');
    setTransactionMessage('Building transaction...');

    try {
      const payload = {
        triggerType: triggerType,
        assetId: collection.assetId,
        ...additionalPayload
      };

      const contracts = [
        {
          type: 11,
          payload: payload
        }
      ];

      setTransactionMessage('Please sign the transaction...');

      const unsignedTx = await web.buildTransaction(contracts);
      const signedTx = await web.signTransaction(unsignedTx);

      setTransactionMessage('Broadcasting transaction...');
      const response = await web.broadcastTransactions([signedTx]);

      console.log('AssetTrigger response:', response);

      setTransactionState('success');
      setTransactionMessage('Transaction successful!');

      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (error) {
      console.error('AssetTrigger error:', error);
      setTransactionState('error');
      setTransactionMessage(error.message || 'Transaction failed');
    }
  };

  // Action handlers
  const handleUpdateLogo = async () => {
    if (!logoUrl) {
      setErrors({ logo: 'Logo URL is required' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.UPDATE_LOGO, { logo: logoUrl });
  };

  const handleUpdateURIs = async () => {
    if (!metadataUri) {
      setErrors({ metadata: 'Metadata URI is required' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.UPDATE_URIS, {
      uris: [{ key: 'metadata', value: metadataUri }]
    });
  };

  const handleUpdateRoyalties = async () => {
    if (royaltyPercentage < 0 || royaltyPercentage > 50) {
      setErrors({ royalty: 'Royalty must be between 0-50%' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.UPDATE_ROYALTIES, {
      royalties: {
        address: royaltyReceiver || walletAddress,
        marketPercentage: Math.round(royaltyPercentage * 100),
        marketFixed: 0,
        itoPercentage: 0,
        itoFixed: 0
      }
    });
  };

  const handleChangeRoyaltiesReceiver = async () => {
    if (!royaltyReceiver || !/^klv1[a-z0-9]{58}$/.test(royaltyReceiver)) {
      setErrors({ royaltyReceiver: 'Invalid Klever address' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.CHANGE_ROYALTIES_RECEIVER, {
      receiver: royaltyReceiver
    });
  };

  const handlePauseCollection = async () => {
    await executeAssetTrigger(TRIGGER_TYPES.PAUSE);
  };

  const handleResumeCollection = async () => {
    await executeAssetTrigger(TRIGGER_TYPES.RESUME);
  };

  const handleChangeOwner = async () => {
    if (!newOwner || !/^klv1[a-z0-9]{58}$/.test(newOwner)) {
      setErrors({ newOwner: 'Invalid Klever address' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.CHANGE_OWNER, {
      receiver: newOwner
    });
  };

  const handleAddRole = async () => {
    if (!roleAddress || !/^klv1[a-z0-9]{58}$/.test(roleAddress)) {
      setErrors({ roleAddress: 'Invalid Klever address' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.ADD_ROLE, {
      role: {
        address: roleAddress,
        hasRoleMint: roleType === 'mint',
        hasRoleSetITOPrices: roleType === 'setITOPrices'
      }
    });
  };

  const handleRemoveRole = async () => {
    if (!roleAddress || !/^klv1[a-z0-9]{58}$/.test(roleAddress)) {
      setErrors({ roleAddress: 'Invalid Klever address' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.REMOVE_ROLE, {
      receiver: roleAddress
    });
  };

  const handleMint = async () => {
    if (mintAmount < 1) {
      setErrors({ mintAmount: 'Amount must be at least 1' });
      return;
    }
    if (!mintReceiver || !/^klv1[a-z0-9]{58}$/.test(mintReceiver)) {
      setErrors({ mintReceiver: 'Invalid Klever address' });
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.MINT, {
      receiver: mintReceiver,
      amount: mintAmount
    });
  };

  const handleStopMint = async () => {
    if (!window.confirm('This action is IRREVERSIBLE! No more NFTs can ever be minted. Are you sure?')) {
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.STOP_NFT_MINT);
  };

  const handleStopRoyaltiesChange = async () => {
    if (!window.confirm('This action is IRREVERSIBLE! Royalties can never be changed again. Are you sure?')) {
      return;
    }
    await executeAssetTrigger(TRIGGER_TYPES.STOP_ROYALTIES_CHANGE);
  };

  const clearError = (field) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const actionItems = [
    { id: 'overview', label: 'Overview', icon: FaLayerGroup },
    { id: 'logo', label: 'Update Logo', icon: FaImage },
    { id: 'metadata', label: 'Metadata URI', icon: FaLink },
    { id: 'royalties', label: 'Royalties', icon: FaPercent },
    { id: 'mint', label: 'Mint NFTs', icon: FaPlus },
    { id: 'pause', label: 'Pause/Resume', icon: FaPause },
    { id: 'roles', label: 'Manage Roles', icon: FaUserPlus },
    { id: 'ownership', label: 'Ownership', icon: FaExchangeAlt },
    { id: 'lock', label: 'Locks', icon: FaLock }
  ];

  return (
    <div className="mcm-overlay">
      <div className="mcm-modal" ref={modalRef}>
        {/* Header */}
        <div className="mcm-header">
          <h2 className="mcm-title">
            <FaCog /> Manage Collection
          </h2>
          <button
            className="mcm-close"
            onClick={onClose}
            disabled={transactionState === 'processing'}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="mcm-body">
          {/* Sidebar */}
          <div className="mcm-sidebar">
            {actionItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`mcm-nav-item ${activeAction === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveAction(item.id);
                    setTransactionState('');
                    setErrors({});
                  }}
                >
                  <Icon />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="mcm-content">
            {/* Overview */}
            {activeAction === 'overview' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title">Collection Overview</h3>
                <div className="mcm-overview">
                  <div className="mcm-overview-img">
                    {collection.logo ? (
                      <img
                        src={collection.logo.replace('ipfs://', 'https://ipfs.io/ipfs/')}
                        alt={collection.name}
                      />
                    ) : (
                      <div className="mcm-placeholder"><FaLayerGroup /></div>
                    )}
                  </div>
                  <div className="mcm-overview-info">
                    <h4>{collection.name}</h4>
                    <p className="mcm-asset-id">{collection.assetId}</p>
                    <div className="mcm-stats-grid">
                      <div className="mcm-stat">
                        <span className="mcm-stat-label">Type</span>
                        <span className="mcm-stat-value">{collection.assetType}</span>
                      </div>
                      <div className="mcm-stat">
                        <span className="mcm-stat-label">Supply</span>
                        <span className="mcm-stat-value">{collection.circulatingSupply?.toLocaleString() || 0}</span>
                      </div>
                      <div className="mcm-stat">
                        <span className="mcm-stat-label">Max Supply</span>
                        <span className="mcm-stat-value">{collection.maxSupply?.toLocaleString() || 'Unlimited'}</span>
                      </div>
                      <div className="mcm-stat">
                        <span className="mcm-stat-label">Royalties</span>
                        <span className="mcm-stat-value">
                          {collection.royalties?.marketPercentage
                            ? `${collection.royalties.marketPercentage / 100}%`
                            : 'None'}
                        </span>
                      </div>
                    </div>
                    <a
                      href={`https://kleverscan.org/asset/${collection.assetId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mcm-explorer-link"
                    >
                      View on KleverScan <FaExternalLinkAlt />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Update Logo */}
            {activeAction === 'logo' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaImage /> Update Collection Logo</h3>
                <p className="mcm-desc">Change the logo image for your collection. Use an IPFS URL for decentralized storage.</p>
                <div className="mcm-form-group">
                  <label className="mcm-label">Logo URL</label>
                  <input
                    type="text"
                    className={`mcm-input ${errors.logo ? 'error' : ''}`}
                    placeholder="ipfs://... or https://..."
                    value={logoUrl}
                    onChange={(e) => { setLogoUrl(e.target.value); clearError('logo'); }}
                  />
                  {errors.logo && <span className="mcm-error">{errors.logo}</span>}
                </div>
                {logoUrl && (
                  <div className="mcm-preview">
                    <span className="mcm-preview-label">Preview:</span>
                    <img
                      src={logoUrl.replace('ipfs://', 'https://ipfs.io/ipfs/')}
                      alt="Logo preview"
                      className="mcm-preview-img"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                <button className="mcm-btn mcm-btn-primary" onClick={handleUpdateLogo}>
                  Update Logo
                </button>
              </div>
            )}

            {/* Update Metadata URI */}
            {activeAction === 'metadata' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaLink /> Update Metadata URI</h3>
                <p className="mcm-desc">Update the base URI for NFT metadata. This should point to your metadata folder on IPFS.</p>
                <div className="mcm-form-group">
                  <label className="mcm-label">Metadata URI</label>
                  <input
                    type="text"
                    className={`mcm-input ${errors.metadata ? 'error' : ''}`}
                    placeholder="ipfs://QmYourMetadataCID/"
                    value={metadataUri}
                    onChange={(e) => { setMetadataUri(e.target.value); clearError('metadata'); }}
                  />
                  {errors.metadata && <span className="mcm-error">{errors.metadata}</span>}
                  <span className="mcm-hint">This is the base URI. Individual NFT metadata will be at URI/1.json, URI/2.json, etc.</span>
                </div>
                <button className="mcm-btn mcm-btn-primary" onClick={handleUpdateURIs}>
                  Update Metadata URI
                </button>
              </div>
            )}

            {/* Royalties */}
            {activeAction === 'royalties' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaPercent /> Royalty Settings</h3>
                <p className="mcm-desc">Configure royalties for secondary sales on marketplaces.</p>
                <div className="mcm-form-group">
                  <label className="mcm-label">Royalty Percentage</label>
                  <div className="mcm-input-suffix">
                    <input
                      type="number"
                      className={`mcm-input ${errors.royalty ? 'error' : ''}`}
                      min={0}
                      max={50}
                      step={0.5}
                      value={royaltyPercentage}
                      onChange={(e) => { setRoyaltyPercentage(parseFloat(e.target.value) || 0); clearError('royalty'); }}
                    />
                    <span className="mcm-suffix">%</span>
                  </div>
                  {errors.royalty && <span className="mcm-error">{errors.royalty}</span>}
                  <span className="mcm-hint">Maximum 50%. Typical range: 2.5% - 10%</span>
                </div>
                <div className="mcm-form-group">
                  <label className="mcm-label">Royalty Receiver</label>
                  <input
                    type="text"
                    className={`mcm-input ${errors.royaltyReceiver ? 'error' : ''}`}
                    placeholder="klv1..."
                    value={royaltyReceiver}
                    onChange={(e) => { setRoyaltyReceiver(e.target.value); clearError('royaltyReceiver'); }}
                  />
                  {errors.royaltyReceiver && <span className="mcm-error">{errors.royaltyReceiver}</span>}
                </div>
                <div className="mcm-btn-group">
                  <button className="mcm-btn mcm-btn-primary" onClick={handleUpdateRoyalties}>Update Royalties</button>
                  <button className="mcm-btn mcm-btn-secondary" onClick={handleChangeRoyaltiesReceiver}>Change Receiver Only</button>
                </div>
              </div>
            )}

            {/* Mint NFTs */}
            {activeAction === 'mint' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaPlus /> Mint NFTs</h3>
                <p className="mcm-desc">Mint new NFTs directly to a wallet address (bypassing ITO).</p>
                <div className="mcm-form-group">
                  <label className="mcm-label">Amount to Mint</label>
                  <input
                    type="number"
                    className={`mcm-input ${errors.mintAmount ? 'error' : ''}`}
                    min={1}
                    value={mintAmount}
                    onChange={(e) => { setMintAmount(parseInt(e.target.value) || 1); clearError('mintAmount'); }}
                  />
                  {errors.mintAmount && <span className="mcm-error">{errors.mintAmount}</span>}
                </div>
                <div className="mcm-form-group">
                  <label className="mcm-label">Receiver Address</label>
                  <input
                    type="text"
                    className={`mcm-input ${errors.mintReceiver ? 'error' : ''}`}
                    placeholder="klv1..."
                    value={mintReceiver}
                    onChange={(e) => { setMintReceiver(e.target.value); clearError('mintReceiver'); }}
                  />
                  {errors.mintReceiver && <span className="mcm-error">{errors.mintReceiver}</span>}
                </div>
                <button className="mcm-btn mcm-btn-primary" onClick={handleMint}>
                  <FaPlus /> Mint {mintAmount} NFT{mintAmount > 1 ? 's' : ''}
                </button>
              </div>
            )}

            {/* Pause/Resume */}
            {activeAction === 'pause' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaPause /> Pause / Resume Collection</h3>
                <p className="mcm-desc">Pausing a collection prevents all transfers and ITO minting.</p>
                <div className="mcm-status-row">
                  <span>Current Status:</span>
                  <span className={`mcm-badge ${collection.attributes?.isPaused ? 'paused' : 'active'}`}>
                    {collection.attributes?.isPaused ? 'Paused' : 'Active'}
                  </span>
                </div>
                <div className="mcm-btn-group">
                  <button
                    className="mcm-btn mcm-btn-warning"
                    onClick={handlePauseCollection}
                    disabled={collection.attributes?.isPaused}
                  >
                    <FaPause /> Pause Collection
                  </button>
                  <button
                    className="mcm-btn mcm-btn-success"
                    onClick={handleResumeCollection}
                    disabled={!collection.attributes?.isPaused}
                  >
                    <FaPlay /> Resume Collection
                  </button>
                </div>
              </div>
            )}

            {/* Manage Roles */}
            {activeAction === 'roles' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaUserPlus /> Manage Roles</h3>
                <p className="mcm-desc">Grant or revoke permissions to other addresses.</p>
                <div className="mcm-form-group">
                  <label className="mcm-label">Wallet Address</label>
                  <input
                    type="text"
                    className={`mcm-input ${errors.roleAddress ? 'error' : ''}`}
                    placeholder="klv1..."
                    value={roleAddress}
                    onChange={(e) => { setRoleAddress(e.target.value); clearError('roleAddress'); }}
                  />
                  {errors.roleAddress && <span className="mcm-error">{errors.roleAddress}</span>}
                </div>
                <div className="mcm-form-group">
                  <label className="mcm-label">Role Type</label>
                  <select className="mcm-select" value={roleType} onChange={(e) => setRoleType(e.target.value)}>
                    <option value="mint">Mint Permission</option>
                    <option value="setITOPrices">Set ITO Prices</option>
                  </select>
                </div>
                <div className="mcm-btn-group">
                  <button className="mcm-btn mcm-btn-success" onClick={handleAddRole}>
                    <FaUserPlus /> Add Role
                  </button>
                  <button className="mcm-btn mcm-btn-warning" onClick={handleRemoveRole}>
                    <FaUserMinus /> Remove Role
                  </button>
                </div>
              </div>
            )}

            {/* Transfer Ownership */}
            {activeAction === 'ownership' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaExchangeAlt /> Transfer Ownership</h3>
                <div className="mcm-warning">
                  <FaInfoCircle />
                  <span><strong>Warning:</strong> Transferring ownership gives full control to another address. You will lose all management capabilities.</span>
                </div>
                <div className="mcm-form-group">
                  <label className="mcm-label">New Owner Address</label>
                  <input
                    type="text"
                    className={`mcm-input ${errors.newOwner ? 'error' : ''}`}
                    placeholder="klv1..."
                    value={newOwner}
                    onChange={(e) => { setNewOwner(e.target.value); clearError('newOwner'); }}
                  />
                  {errors.newOwner && <span className="mcm-error">{errors.newOwner}</span>}
                </div>
                <button className="mcm-btn mcm-btn-danger" onClick={handleChangeOwner}>
                  <FaExchangeAlt /> Transfer Ownership
                </button>
              </div>
            )}

            {/* Permanent Locks */}
            {activeAction === 'lock' && (
              <div className="mcm-panel">
                <h3 className="mcm-panel-title"><FaLock /> Permanent Locks</h3>
                <div className="mcm-danger">
                  <FaBan />
                  <span><strong>DANGER ZONE:</strong> These actions are IRREVERSIBLE! Once executed, they cannot be undone.</span>
                </div>
                <div className="mcm-lock-list">
                  <div className="mcm-lock-item">
                    <div className="mcm-lock-info">
                      <h4>Stop NFT Minting</h4>
                      <p>Permanently disable minting. No more NFTs can ever be created.</p>
                    </div>
                    <button className="mcm-btn mcm-btn-danger" onClick={handleStopMint}>
                      <FaLock /> Stop Forever
                    </button>
                  </div>
                  <div className="mcm-lock-item">
                    <div className="mcm-lock-info">
                      <h4>Lock Royalties</h4>
                      <p>Permanently lock royalty settings. Cannot be changed after.</p>
                    </div>
                    <button className="mcm-btn mcm-btn-danger" onClick={handleStopRoyaltiesChange}>
                      <FaLock /> Lock Forever
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction Status */}
            {transactionState && (
              <div className={`mcm-tx-status mcm-tx-${transactionState}`}>
                {transactionState === 'processing' && <FaSpinner className="mcm-spin" />}
                {transactionState === 'success' && <FaCheckCircle />}
                {transactionState === 'error' && <FaTimesCircle />}
                <span>{transactionMessage}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCollectionModal;
